import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { SHIPPING_FLAT } from "../mock/mock";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
];

export default function Checkout() {
  const { items, subtotal, discount, total } = useCart();
  const grandTotal = total + SHIPPING_FLAT;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testMode, setTestMode] = useState(false);
  useEffect(() => {
    api.get("/config").then(({ data }) => setTestMode(!data.stripe_live)).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    name: "", email: "", address1: "", city: "", region: "", zip: "", country: "US", phone: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
        origin_url: window.location.origin,
        customer: form,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed. Please try again.";
      toast("Checkout error", { description: msg });
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-28 md:pt-36 pb-32 text-center px-4">
        <h1 className="font-display text-4xl md:text-5xl text-white">Your bag is empty</h1>
        <p className="mt-3 text-white/60">Add a piece before checking out.</p>
        <Link to="/shop" className="inline-block mt-6 bg-neon text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full">
          Shop the Drop
        </Link>
      </main>
    );
  }

  const input = "w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-neon";

  return (
    <main className="pt-20 md:pt-28 pb-20">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8">
        <Link to="/shop" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white/60 hover:text-neon transition">
          <ArrowLeft size={16} /> Continue shopping
        </Link>
        <h1 className="font-display text-4xl md:text-6xl text-white mt-4 mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          {/* Form */}
          <form onSubmit={submit} className="space-y-5">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-neon">Contact</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" className={input} value={form.name} onChange={set("name")} />
              <input required type="email" placeholder="Email" className={input} value={form.email} onChange={set("email")} />
            </div>

            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-neon pt-2">Shipping address</h2>
            <input required placeholder="Street address" className={input} value={form.address1} onChange={set("address1")} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="City" className={input} value={form.city} onChange={set("city")} />
              <input required placeholder="State / Region" className={input} value={form.region} onChange={set("region")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="ZIP / Postal code" className={input} value={form.zip} onChange={set("zip")} />
              <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                <SelectTrigger className="bg-white/5 border-white/15 text-white h-[46px]">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/15 text-white">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input placeholder="Phone (optional)" className={input} value={form.phone} onChange={set("phone")} />

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-neon text-black font-bold uppercase tracking-[0.16em] text-sm py-4 rounded-full hover:brightness-95 transition disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : <><Lock size={16} /> Pay securely with Stripe</>}
            </button>
            <p className="text-[11px] text-white/40 text-center flex items-center justify-center gap-1.5">
              <Lock size={11} /> Secure checkout · payments processed by Stripe.
            </p>
            {testMode && (
              <p className="text-[11px] text-white/30 text-center" data-testid="checkout-test-note">
                Test mode · use card 4242 4242 4242 4242, any future date &amp; CVC.
              </p>
            )}
          </form>

          {/* Summary */}
          <div className="rounded-xl border border-white/10 bg-card p-6 h-fit">
            <h2 className="font-display text-2xl text-white mb-4">Your bag</h2>
            <div className="space-y-4 max-h-[340px] overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <img src={it.image} alt={it.name} className="w-16 h-20 object-cover rounded-md border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">{it.brand}</p>
                    <h4 className="text-sm font-semibold text-white truncate">{it.name}</h4>
                    <p className="text-xs text-white/50">Size {it.size} · Qty {it.qty}</p>
                  </div>
                  <span className="text-sm font-bold text-white">${(it.price * it.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-5 pt-5 space-y-2">
              <div className="flex justify-between text-sm text-white/60"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-neon" data-testid="checkout-bundle-savings"><span>Bundle savings</span><span>-${discount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-sm text-white/60"><span>Shipping</span><span data-testid="checkout-shipping">${SHIPPING_FLAT.toFixed(2)}</span></div>
              <div className="flex justify-between text-lg font-bold text-white pt-2"><span>Total</span><span data-testid="checkout-total">${grandTotal.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
